/* eslint-disable max-len */
/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import { omit } from 'lodash';
import { compareArray, useAdminParams } from 'utils/helpers';
import { ACCOUNT_USER, Company, Lead, Logs } from 'utils/types';
import { ACCESS_LEVEL, PERMISSION_KEYS } from 'utils/constants';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import Cookies from 'js-cookie';

import {
  Actor,
  ActivityLogData,
  ActivityLogDataDetails,
  ActivityLogDataArray,
} from '../ActivityLog';

const DealLogs = {
  DEAL_CREATED: {
    title: 'Create Deal',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> created the Deal.
      </p>
    ),
  },
  DEAL_UPDATED: {
    title: 'Edit Deal',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const _meta = omit(meta, [
        'updatedAt',
        'accountId',
        'leadId',
        'accountUserId',
        'companyId',
        'stageId',
      ]);
      const _prevMeta = omit(prevMeta, [
        'updatedAt',
        'accountId',
        'leadId',
        'accountUserId',
        'companyId',
        'stageId',
      ]);

      const data = {
        ..._meta,
        tagId:
          _meta.tagId.map(({ tag }: Record<string, any>) => tag).join(', ') ??
          [],
      };

      const prevData = {
        ..._prevMeta,
        tagId:
          _prevMeta?.tagId
            .map(({ tag }: Record<string, any>) => tag)
            .join(', ') ?? [],
      };

      return (
        <>
          <p>
            <Actor data={actor} /> updated the Deal.
          </p>
          <ActivityLogData data={data} prevData={prevData} />
        </>
      );
    },
  },
  DEAL_DELETED: {
    title: 'Delete Deal',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> deleted the Deal.
      </p>
    ),
  },
  DEAL_STATUS_UPDATED: {
    title: 'Update Deal Status',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const data = { status: meta.stageId.name };
      const prevData = { status: prevMeta.stageId.name };

      return (
        <>
          <p>
            <Actor data={actor} /> updated the status of the Deal.
          </p>
          <ActivityLogData data={data} prevData={prevData} />
        </>
      );
    },
  },
  DEAL_ASSIGNED: {
    title: 'Associate Deal To Owner',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewAccountUserAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.ACCOUNT,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/settings/teams/people?id=${actor?.actorId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
      }, [actor?.role, actor?.actorId, accountId, accountUserId, role]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewAccountUserAll;
      }, [actor?.role, canViewAccountUserAll]);

      const _accountUserId =
        prevMeta?.accountUserId.map(({ _id }: ACCOUNT_USER) =>
          _id.toString(),
        ) ?? [];
      const _newAccountUserId = meta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );

      const { new: newArray } = compareArray(_accountUserId, _newAccountUserId);

      return (
        <p>
          <Actor data={actor} /> assgined the Deal to Deal Owner(s){' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={meta.accountUserId.filter(({ _id }: ACCOUNT_USER) =>
              newArray?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_REMOVED_ASSIGNED: {
    title: 'Remove Associate Deal To Owner',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewAccountUserAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.ACCOUNT,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/settings/teams/people?id=${actor?.actorId}`;

        return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
      }, [actor?.role, actor?.actorId, accountId, accountUserId, role]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewAccountUserAll;
      }, [actor?.role, canViewAccountUserAll]);

      const _accountUserId =
        prevMeta?.accountUserId.map(({ _id }: ACCOUNT_USER) =>
          _id.toString(),
        ) ?? [];
      const _newAccountUserId = meta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );

      const { remove } = compareArray(_accountUserId, _newAccountUserId);

      return (
        <p>
          <Actor data={actor} /> unassigned the Deal from Deal Owner(s){' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={prevMeta.accountUserId.filter(({ _id }: ACCOUNT_USER) =>
              remove?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_LEAD_ASSIGNED: {
    title: 'Associate Deal To Lead',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return '/contact/leads/';

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/`;
        }

        if (actor?.role === 'account') return 'contact/leads/';

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/`;
      }, [actor?.role, role, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      const _leadId =
        prevMeta?.leadId.map(({ _id }: Lead) => _id.toString()) ?? [];
      const _newLeadId = meta.leadId.map(({ _id }: Lead) => _id.toString());

      const { new: newArray } = compareArray(_leadId, _newLeadId);

      return (
        <p>
          <Actor data={actor} /> assgined the Deal to Lead(s){' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={meta.leadId.filter(({ _id }: Lead) =>
              newArray?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_LEAD_REMOVED_ASSIGNED: {
    title: 'Remove Associate Deal To Lead',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return 'contact/leads/';

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/`;
        }

        if (actor?.role === 'account') return 'contact/leads/';

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/`;
      }, [actor?.role, role, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      const _leadId =
        prevMeta?.leadId.map(({ _id }: Lead) => _id.toString()) ?? [];
      const _newLeadId = meta.leadId.map(({ _id }: Lead) => _id.toString());

      const { remove } = compareArray(_leadId, _newLeadId);

      return (
        <p>
          <Actor data={actor} /> unassgined the Deal from Lead(s){' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={prevMeta.leadId.filter(({ _id }: Lead) =>
              remove?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_COMPANY_ASSIGNED: {
    title: 'Associate Deal To Company',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewCompaniessAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.COMPANIES,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/contact/companies/`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/`;
        }

        if (actor?.role === 'account') return `/contact/companies/`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/`;
      }, [actor?.role, role, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewCompaniessAll;
      }, [actor?.role, canViewCompaniessAll]);

      const _companyId = prevMeta?.companyId
        ? [prevMeta?.companyId].map(({ _id }: Company) => _id.toString())
        : [];
      const _newCompanyId = meta?.companyId
        ? [meta?.companyId].map(({ _id }: Company) => _id.toString())
        : [];

      const { new: newArray } = compareArray(_companyId, _newCompanyId);

      return (
        <p>
          <Actor data={actor} /> assgined the Deal to{' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={[meta?.companyId].filter(({ _id }: Company) =>
              newArray?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_COMPANY_REMOVED_ASSIGNED: {
    title: 'Remove Associate Deal To Company',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewCompaniessAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.COMPANIES,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return '/contact/companies/';

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/`;
        }

        if (actor?.role === 'account') return '/contact/companies/';

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/`;
      }, [actor?.role, role, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewCompaniessAll;
      }, [actor?.role, canViewCompaniessAll]);

      const _companyId = prevMeta?.companyId
        ? [prevMeta?.companyId].map(({ _id }: Company) => _id.toString())
        : [];
      const _newCompanyId = meta?.companyId
        ? [meta?.companyId].map(({ _id }: Company) => _id.toString())
        : [];

      const { remove } = compareArray(_companyId, _newCompanyId);

      return (
        <p>
          <Actor data={actor} /> unassgined the Deal from{' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={[prevMeta?.companyId].filter(({ _id }: Company) =>
              remove?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  DEAL_TASK_CREATED: {
    title: 'Add Task',
    element: ({ actor, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/sales/deals/${referenceId}/tasks`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/tasks`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/tasks`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/tasks`;
      }, [actor?.role, role, referenceId, accountId, accountUserId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added Task to Deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_TASK_UPDATED: {
    title: 'Edit Task',
    element: ({ actor, meta: { task, deal } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${deal?.id}tasks/${task?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?.id}tasks/${task?._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${deal?.id}tasks/${task?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?.id}tasks/${task?._id}`;
      }, [actor?.role, role, accountId, accountUserId, deal?.id, task?._id]);

      return (
        <>
          <p>
            <Actor data={actor} /> updated the Task.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </>
      );
    },
  },
  DEAL_TASK_DELETED: {
    title: 'Delete Task',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> deleted the Task.
      </p>
    ),
  },
  DEAL_TASK_STATUS_UPDATED: {
    title: 'Update Task Status',
    element: ({
      actor,
      meta: {
        task: { meta, prevMeta },
      },
    }: Logs) => {
      const data = { status: meta?.status };
      const prevData = { status: prevMeta?.status };

      return (
        <>
          <p>
            <Actor data={actor} /> updated the status of the Task.
          </p>
          <ActivityLogData data={data} prevData={prevData} />
        </>
      );
    },
  },
  DEAL_EVENT_CREATED: {
    title: 'Add Event',
    element: ({ actor, meta: { event } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/calendar/calendar/events?id=${event?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
        }

        if (actor?.role === 'account')
          return `/calendar/calendar/events?id=${event?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
      }, [actor?.role, role, event, accountId, accountUserId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added an Event to Deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_EVENT_UPDATED: {
    title: 'Edit Event',
    element: ({ actor, meta: { event } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/calendar/calendar/events?id=${event?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
        }

        if (actor?.role === 'account')
          return `/calendar/calendar/events?id=${event?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
      }, [actor?.role, role, event, accountId, accountUserId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated the Event.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_EVENT_DELETED: {
    title: 'Delete Event',
    element: ({ actor, meta: { event } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/calendar/calendar/events?id=${event?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
        }

        if (actor?.role === 'account')
          return `/calendar/calendar/events?id=${event?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/calendar/calendar/events?id=${event?._id}`;
      }, [actor?.role, role, event, accountId, accountUserId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted the Event.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_ADDED: {
    title: 'Add Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_UPDATED: {
    title: 'Edit Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_DELETED: {
    title: 'Delete Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        actor?.actorId,
        comment,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        attachment._id,
        referenceId,
        role,
        accountId,
        accountUserId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_COMMENT_ADDED: {
    title: 'Add Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_COMMENT_UPDATED: {
    title: 'Edit Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_COMMENT_DELETED: {
    title: 'Delete Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
        actor?.actorId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_COMMENT_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/sales/deals/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        attachment._id,
        referenceId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_ATTACHMENT_ADDED: {
    title: 'Add Deal Attachment',
    element: ({ actor, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        actor?.actorId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> attached a file to a deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  DEAL_ATTACHMENT_DELETED: {
    title: 'Delete Deal Attachment',
    element: ({ actor, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${referenceId}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        actor?.actorId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
};

export default DealLogs;
